import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from 'app/hooks';
import useIsTourBlocking from 'pages/dashboard/tour/useIsTourBlocking';
import If from 'components/If';
import { finishAccountSetupWaitingPeriod } from 'utils/constants';
import {
  selectIsDataCollectionModalClosed,
  selectIsEligibleForDataCollection,
} from 'services/misc/miscSelectors';
import {
  selectIsExpiryNoticeModalClosed,
  selectIsEligibleForCtp,
  selectIsFinishAccountSetupModalClosed,
  selectIsEligibleForFinishAccountSetup,
} from 'services/ctp/ctpSelectors';
import { selectUserSince } from 'services/user/userSelectors';
import useUnlockInsuranceBootstrap from 'modules/unlock-insurance/hooks/useUnlockInsuranceBootstrap';
import {
  selectIsUnlockInsuranceEligible,
  selectIsUnlockInsuranceModalClosed,
  selectIsUnlockInsuranceSurfaceOpen,
} from 'modules/unlock-insurance/services/unlockInsuranceSelectors';
import MembershipExpiryNotice from './MembershipExpiryNotice';
import MissingDataCollection from './MissingDataCollection';
import FinishAccountSetup from './FinishAccountSetup';
import UnlockInsurance from './UnlockInsurance';

type sequenceProps = 'unlockInsurance' | 'pii' | 'accountSetup' | 'ctp' | null;

function SessionModalSequence() {
  const [currentModal, setCurrentModal] = useState<sequenceProps>(null);
  const isEligibleForDataCollection = useAppSelector(
    selectIsEligibleForDataCollection,
  );

  const isDataCollectionModalClosed = useAppSelector(
    selectIsDataCollectionModalClosed,
  );
  const isEligibleForCtp = useAppSelector(selectIsEligibleForCtp);
  const isExpiryNoticeModalClosed = useAppSelector(
    selectIsExpiryNoticeModalClosed,
  );
  const isEligibleForFinishAccountSetup = useAppSelector(
    selectIsEligibleForFinishAccountSetup,
  );
  const isFinishAccountSetupModalClosed = useAppSelector(
    selectIsFinishAccountSetupModalClosed,
  );
  const userSince = useAppSelector(selectUserSince);
  const isEligibleForUnlockInsurance = useAppSelector(
    selectIsUnlockInsuranceEligible,
  );
  const isUnlockInsuranceModalClosed = useAppSelector(
    selectIsUnlockInsuranceModalClosed,
  );
  const isUnlockSurfaceOpen = useAppSelector(
    selectIsUnlockInsuranceSurfaceOpen,
  );

  /* FR-038: the module owns its two reads, issued from this mount point. */
  useUnlockInsuranceBootstrap();

  /* The dashboard tour takes priority over every dialog in this sequence
     (product rule). One shared signal rather than a local re-derivation, so
     this cannot drift from the other popups that consult it. */
  const isTourBlocking = useIsTourBlocking();

  /* Check is user eligible to collect the missing PII */
  const isDataCollectionEligible = useMemo(() => {
    return isEligibleForDataCollection && !isDataCollectionModalClosed;
  }, [isEligibleForDataCollection, isDataCollectionModalClosed]);

  /* Check is user eligible for CTP */
  const isMemberShipExpiryNoticeModalEligible = useMemo(() => {
    return isEligibleForCtp && !isExpiryNoticeModalClosed;
  }, [isEligibleForCtp, isExpiryNoticeModalClosed]);

  /* Check is user eligible for the unlock-insurance prompt */
  const isUnlockInsuranceEligible = useMemo(() => {
    return isEligibleForUnlockInsurance && !isUnlockInsuranceModalClosed;
  }, [isEligibleForUnlockInsurance, isUnlockInsuranceModalClosed]);

  /* Check is user eligible for finish account setup */
  const isFinishAccountSetupModalEligible = useMemo(() => {
    const daysDiff = dayjs().diff(userSince, 'day');
    const showToday =
      daysDiff > finishAccountSetupWaitingPeriod &&
      (daysDiff === finishAccountSetupWaitingPeriod + 1 || daysDiff % 30 === 0);

    return (
      isEligibleForFinishAccountSetup &&
      showToday &&
      !isFinishAccountSetupModalClosed
    );
  }, [
    isEligibleForFinishAccountSetup,
    isFinishAccountSetupModalClosed,
    userSince,
  ]);

  const modalSequence = useMemo(() => {
    const list: sequenceProps[] = [];

    if (isTourBlocking) {
      return list;
    }
    /* A surface opened from the banner or an Offers & Upgrades plan card is not a queue entry,
       so the queue cannot see it. Hold the queue, exactly as for the tour. */
    if (isUnlockSurfaceOpen) {
      return list;
    }
    /* Pushed first: the list is consumed with shift(), so order here is display order. */
    if (isUnlockInsuranceEligible) {
      list.push('unlockInsurance');
    }
    if (isDataCollectionEligible) {
      list.push('pii');
    }
    if (isFinishAccountSetupModalEligible) {
      list.push('accountSetup');
    }
    if (isMemberShipExpiryNoticeModalEligible) {
      list.push('ctp');
    }

    return list;
  }, [
    isTourBlocking,
    isUnlockSurfaceOpen,
    isUnlockInsuranceEligible,
    isDataCollectionEligible,
    isMemberShipExpiryNoticeModalEligible,
    isFinishAccountSetupModalEligible,
  ]);

  const handleNextModalSequence = () => {
    let modalName = null;
    if (modalSequence.length) {
      modalName = modalSequence.shift();
    }
    setCurrentModal(modalName ?? null);
  };

  useEffect(() => {
    if (
      modalSequence.length &&
      (!currentModal || modalSequence.includes(currentModal))
    ) {
      handleNextModalSequence();
    }
  }, [currentModal, modalSequence]);

  return (
    <>
      <If condition={currentModal === 'unlockInsurance'}>
        <UnlockInsurance
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
      <If condition={currentModal === 'pii'}>
        <MissingDataCollection
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
      <If condition={currentModal === 'accountSetup'}>
        <FinishAccountSetup
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
      <If condition={currentModal === 'ctp'}>
        <MembershipExpiryNotice
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
    </>
  );
}

export default SessionModalSequence;
